import telemetryContract from './telemetry-events.generated.json';

const contractEntries = Object.entries(telemetryContract.events);
const DEMO_EVENTS = new Set(contractEntries.filter(([, event]) => event.class === 'demo').map(([name]) => name));
const ESSENTIAL_EVENTS = new Set(contractEntries.filter(([, event]) => event.class === 'essential').map(([name]) => name));
const VERSION = /^[0-9]{1,4}\.[0-9]{1,4}(?:\.[0-9]{1,4})?(?:[-+][a-z0-9.-]{1,32})?$/i;

const OPERATIONAL_INCIDENT_EVENTS = Object.freeze({
  'bridge.error': {
    eventName: 'bridge_protocol_failed',
    reasonCode: 'bridge_context_unavailable',
  },
  'bridge.timeout': {
    eventName: 'bridge_timeout',
    reasonCode: 'bridge_response_timeout',
  },
});

function durationBucket(durationMs) {
  if (!Number.isFinite(durationMs) || durationMs < 0) return 'unknown';
  if (durationMs < 30000) return 'under_30s';
  if (durationMs < 120000) return '30_120s';
  if (durationMs < 300000) return '2_5m';
  if (durationMs < 900000) return '5_15m';
  return '15m_plus';
}

function stageForEvent(eventName) {
  if (eventName === 'wizard_completed' || eventName === 'publish_succeeded') return 'completed';
  if (eventName === 'wizard_preview_opened' || eventName === 'preview_opened') return 'preview_opened';
  if (eventName.indexOf('wizard_') === 0) return 'wizard_started';
  if (eventName === 'demo_admin_loaded') return 'admin_loaded';
  return null;
}

const STAGE_ORDER = ['admin_loaded', 'wizard_started', 'preview_opened', 'completed'];

function randomEventId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `evt_${crypto.randomUUID().replace(/-/g, '')}`;
  }
  return `evt_${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

function validPropertyValue(schema, value) {
  if (!schema) return false;
  if (schema.type === 'boolean') return typeof value === 'boolean';
  if (schema.type === 'integer') {
    return Number.isInteger(value) && value >= schema.minimum && value <= schema.maximum;
  }
  if (schema.type === 'enum') return typeof value === 'string' && schema.values.includes(value);
  if (schema.type === 'version') return typeof value === 'string' && VERSION.test(value);
  return false;
}

function sanitizeProperties(eventName, properties) {
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return {};
  const event = telemetryContract.events[eventName];
  if (!event) return {};
  const allowed = new Set([...telemetryContract.base_properties, ...event.properties]);
  return Object.keys(properties).reduce((result, key) => {
    if (!allowed.has(key)) return result;
    const value = properties[key];
    if (validPropertyValue(telemetryContract.properties[key], value)) {
      result[key] = value;
    }
    return result;
  }, {});
}

function sanitizeBaseProperties(properties) {
  return telemetryContract.base_properties.reduce((result, key) => {
    const value = properties[key];
    if (validPropertyValue(telemetryContract.properties[key], value)) result[key] = value;
    return result;
  }, {});
}

class TelemetryClient {
  constructor() {
    this.reset();
  }

  configure({ policy, session, appVersion, moduleVersion, fetchImpl } = {}) {
    this.reset();
    if (!policy || !session) return;
    this.policy = policy;
    this.session = session;
    this.fetchImpl = fetchImpl || (typeof fetch === 'function' ? fetch : null);
    this.baseProperties = sanitizeBaseProperties({
      app_version: appVersion,
      module_version: moduleVersion,
    });
    this.demoStartedAt = Date.now();
    this.furthestStage = 'admin_loaded';
  }

  reset() {
    this.policy = { mode: 'essential', policyVersion: 0, demoSessionId: null };
    this.session = null;
    this.fetchImpl = null;
    this.baseProperties = {};
    this.demoStartedAt = null;
    this.furthestStage = null;
  }

  canCapture(eventName) {
    if (!this.session || !this.fetchImpl) return false;
    const expiresAtMs = Date.parse(String(this.policy.expiresAt || ''));
    if (!Number.isFinite(expiresAtMs) || expiresAtMs <= Date.now()) return false;
    if (this.policy.mode === 'demo_full') return DEMO_EVENTS.has(eventName);
    if (this.policy.mode === 'essential') return ESSENTIAL_EVENTS.has(eventName);
    return false;
  }

  capture(eventName, properties = {}) {
    if (!this.canCapture(eventName)) return Promise.resolve(false);
    const baseUrl = String(this.session.gatewayBaseUrl || '').replace(/\/+$/, '');
    const token = String(this.session.gatewayToken || '');
    const accessToken = String(this.session.gatewayAccessToken || '');
    if (!baseUrl || !token || !accessToken) return Promise.resolve(false);

    const nextStage = stageForEvent(eventName);
    if (nextStage && STAGE_ORDER.indexOf(nextStage) > STAGE_ORDER.indexOf(this.furthestStage)) {
      this.furthestStage = nextStage;
    }
    const body = {
      eventId: randomEventId(),
      eventName,
      occurredAt: new Date().toISOString(),
      policyVersion: this.policy.policyVersion,
      demoSessionId: this.policy.mode === 'demo_full'
        ? this.policy.demoSessionId
        : null,
      properties: {
        ...sanitizeProperties(eventName, properties),
        ...this.baseProperties,
      },
    };

    return this.fetchImpl(
      `${baseUrl}/v1/installations/${encodeURIComponent(token)}/telemetry/events`,
      {
        method: 'POST',
        credentials: 'omit',
        keepalive: true,
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      },
    ).then(response => Boolean(response && response.ok)).catch(() => false);
  }

  captureOperationalIncident(incident) {
    if (!incident || typeof incident !== 'object' || Array.isArray(incident)) {
      return Promise.resolve(false);
    }
    const mapping = OPERATIONAL_INCIDENT_EVENTS[incident.event];
    if (!mapping) return Promise.resolve(false);

    return this.capture(mapping.eventName, {
      normalized_reason_code: mapping.reasonCode,
      operation_duration_bucket: durationBucket(incident.durationMs),
    });
  }

  endDemoSession() {
    if (this.policy.mode !== 'demo_full' || this.demoStartedAt === null) return Promise.resolve(false);
    return this.capture('demo_session_ended', {
      duration_bucket: durationBucket(Date.now() - this.demoStartedAt),
      furthest_stage: this.furthestStage || 'admin_loaded',
    });
  }
}

export const telemetryClient = new TelemetryClient();

export const __test_only = {
  telemetryContract,
  validPropertyValue,
  DEMO_EVENTS,
  ESSENTIAL_EVENTS,
  durationBucket,
  stageForEvent,
  sanitizeProperties,
  OPERATIONAL_INCIDENT_EVENTS,
  TelemetryClient,
};
